// ─────────────────────────────────────────────────────────────────────────────
//  PharmacyUnpaidSettings — "Unpaid Prescription Visibility"
//
//  Controls whether the dispensary sees prescriptions the patient has not paid
//  for yet, and whether it may dispense them.
//
//  The three options are worded by what the pharmacist SEES and what they MAY
//  DO, because those are the two things that actually differ and the mode names
//  ("view", "serve") do not carry that on their own.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Radio, RadioGroup, FormControlLabel, FormControl, Typography,
  Button, CircularProgress, Alert, Chip,
} from '@mui/material';
import {
  SaveOutlined as SaveIcon,
  VisibilityOffOutlined as HideIcon,
  VisibilityOutlined as ViewIcon,
  LocalPharmacyOutlined as ServeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import { pharmacyUnpaidVisibility } from '../BaseURL';

const MODES = [
  {
    value: 'hide',
    icon: <HideIcon fontSize="small" />,
    title: 'Hide unpaid prescriptions',
    sees: 'The pharmacist sees nothing until the cash desk records payment.',
    may:  'Nothing can be dispensed before payment.',
    isDefault: true,
  },
  {
    value: 'view',
    icon: <ViewIcon fontSize="small" />,
    title: 'Show unpaid, but do not allow dispensing',
    sees: 'The pharmacist sees the order marked UNPAID, and can check stock and tell the patient what was prescribed.',
    may:  'Dispensing stays blocked until payment is recorded.',
  },
  {
    value: 'serve',
    icon: <ServeIcon fontSize="small" />,
    title: 'Show unpaid and allow dispensing',
    sees: 'The pharmacist sees the order marked UNPAID.',
    may:  'It can be dispensed before payment — for staff, credit patients and emergencies.',
  },
];

const PharmacyUnpaidSettings = () => {
  const [mode, setMode]       = useState('hide');
  const [saved, setSaved]     = useState('hide');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(pharmacyUnpaidVisibility);
      const v = res.data?.unpaidVisibility;
      const next = ['hide', 'view', 'serve'].includes(v) ? v : 'hide';
      setMode(next);
      setSaved(next);
    } catch (err) {
      // Fail closed in the UI too: show 'hide' so nobody reads this screen as
      // "the gate is open" when we could not actually read the setting.
      setMode('hide');
      setSaved('hide');
      setError(
        err.response?.status === 404
          ? 'This server does not have the setting yet — deploy the latest backend.'
          : (err.response?.data?.message || err.message || 'Could not load the setting.')
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (mode === saved) return;

    if (mode === 'serve') {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Allow dispensing before payment?',
        html: `
          <div style="text-align:left;font-size:0.9rem">
            <p>The pharmacy will be able to hand out medication that has not been paid for.</p>
            <p style="color:#b45309">Only choose this if your facility settles afterwards
               — staff, credit patients or emergencies.</p>
          </div>`,
        showCancelButton: true,
        confirmButtonColor: '#b45309',
        cancelButtonColor: '#9e9e9e',
        confirmButtonText: 'Yes, allow it',
      });
      if (!confirm.isConfirmed) return;
    }

    setSaving(true);
    try {
      const res = await axios.put(pharmacyUnpaidVisibility, { unpaidVisibility: mode });
      if (res.data?.success) {
        setSaved(res.data.unpaidVisibility);
        setMode(res.data.unpaidVisibility);
        await Swal.fire({
          icon: 'success', title: 'Saved',
          text: 'The dispensary will pick this up within 30 seconds.',
          timer: 2200, showConfirmButton: false,
        });
      } else {
        throw new Error(res.data?.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        icon: 'error', title: 'Save Failed',
        text: err.response?.data?.message || err.message,
        confirmButtonColor: '#c0392b',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="warning" sx={{ fontSize: '0.82rem' }}>{error}</Alert>}

      <Typography sx={{ fontSize: '0.82rem', color: '#475569' }}>
        A prescription counts as unpaid only while its payment status is
        <strong> pending</strong>. Credit and insurance-covered items are already
        treated as paid and are unaffected by this setting.
      </Typography>

      <FormControl>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODES.map(m => (
            <Box
              key={m.value}
              sx={{
                border: '1px solid',
                borderColor: mode === m.value ? '#11998e' : '#e2e8f0',
                bgcolor: mode === m.value ? 'rgba(17,153,142,0.04)' : '#fff',
                borderRadius: 2, p: 1.5, mb: 1, transition: 'all .15s',
              }}
            >
              <FormControlLabel
                value={m.value}
                control={<Radio size="small" sx={{ color: '#11998e', '&.Mui-checked': { color: '#11998e' } }} />}
                sx={{ alignItems: 'flex-start', m: 0 }}
                label={
                  <Box sx={{ pt: 0.25 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                      {m.icon}
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
                        {m.title}
                      </Typography>
                      {m.isDefault && (
                        <Chip label="Default" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(100,116,139,0.12)', color: '#475569' }} />
                      )}
                      {saved === m.value && (
                        <Chip label="Active" size="small"
                          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(16,185,129,0.14)', color: '#15803D' }} />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>Sees:</strong> {m.sees}
                    </Typography>
                    <Typography sx={{ fontSize: '0.76rem', color: '#475569' }}>
                      <strong>May do:</strong> {m.may}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ))}
        </RadioGroup>
      </FormControl>

      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || mode === saved}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, background: '#11998e',
                '&:hover': { background: '#0e8a7f' } }}
        >
          {saving ? 'Saving…' : mode === saved ? 'Saved' : 'Save change'}
        </Button>
      </Stack>
    </Stack>
  );
};

export default PharmacyUnpaidSettings;
